"use client";

import { useLocale } from "next-intl";
import { useCallback } from "react";
import { useWidgetConfig } from "./use-widget-config";

/**
 * Decide whether an operator-configured text (greeting, block title, …)
 * should be shown to the current visitor, or whether the widget's bundled
 * per-locale translation should be used instead.
 *
 * Operator-stored text is treated as written in the integration's
 * `defaultLocale` (the language the operator chose as their primary). When
 * the visitor's UI locale shares a base language, the operator's wording
 * wins — they took the time to write it, after all. When it doesn't, the
 * bundled translation steps in so an English-only operator greeting never
 * leaks into a French / Japanese / etc. session.
 *
 * Region tags are normalised: `pt-BR` and `pt` both count as Portuguese,
 * so an operator default of `pt-BR` still feeds operator text to a
 * `pt`-locale visitor (and vice-versa). Region-specific overrides will
 * arrive when the BE adds per-locale operator text — until then this
 * base-language match is the closest the widget can get on its own.
 *
 * Empty / whitespace-only operator text triggers the fallback too — that
 * matches the existing block-title behaviour (`block.title?.trim() || t(...)`)
 * so an unset title keeps showing the bundled default in EVERY locale.
 *
 * For greeting, where empty operator text means "this line was deliberately
 * skipped, render nothing" rather than "back-fill from the bundle", inline
 * the locale check + an explicit empty handler instead of using this helper.
 */
export function localizeConfigText(
  operatorText: string | null | undefined,
  fallback: string,
  visitorLocale: string,
  integrationDefaultLocale: string | null | undefined,
): string {
  const trimmed = operatorText?.trim() ?? "";
  if (!trimmed) return fallback;
  if (matchesBaseLocale(visitorLocale, integrationDefaultLocale ?? "en")) return trimmed;
  return fallback;
}

export function matchesBaseLocale(a: string, b: string): boolean {
  return baseOf(a) === baseOf(b);
}

function baseOf(locale: string): string {
  return locale.toLowerCase().split("-")[0];
}

/**
 * Hook flavour — pulls visitor locale + integration default from context
 * so call sites don't have to thread them through. Returns a stable
 * `(operatorText, fallback) => string` closure that re-creates on locale
 * change so memoised consumers re-render correctly.
 */
export function useConfigLocalizer() {
  const locale = useLocale();
  const { config } = useWidgetConfig();
  const defaultLocale = config.defaultLocale ?? "en";
  return useCallback(
    (operatorText: string | null | undefined, fallback: string) =>
      localizeConfigText(operatorText, fallback, locale, defaultLocale),
    [locale, defaultLocale],
  );
}
